/**
 * Web Mercator (EPSG:3857) and slippy-tile math.
 *
 * Pure functions only — no engine types, no DOM. Ported and tightened from
 * smartmaps' `src/store/modules/georef.ts`.
 *
 * Two coordinate conventions are used throughout:
 *  - "normalised mercator": x and y in 0..1 over the whole world, y = 0 at the
 *    north edge. This is the tile grid scaled down by 2^z, and it is the space
 *    in which tile rows are evenly spaced.
 *  - "tile coords": normalised mercator multiplied by 2^z.
 */

/** WGS84 semi-major axis, and the sphere radius Web Mercator is defined on. */
export const EARTH_RADIUS = 6378137;

/** Equatorial circumference: 40075016.6856 m. */
export const EARTH_CIRCUMFERENCE = 2 * Math.PI * EARTH_RADIUS;

/**
 * The latitude at which the Mercator projection is truncated to make the world
 * square. Beyond this, y would run off to infinity.
 */
export const MAX_LATITUDE = 85.051128779806604;

export const DEG_TO_RAD = Math.PI / 180;
export const RAD_TO_DEG = 180 / Math.PI;

/** Clamp to the projectable range. Poles are not representable in Mercator. */
export function clampLatitude(lat: number): number {
  return Math.min(MAX_LATITUDE, Math.max(-MAX_LATITUDE, lat));
}

/** Wrap a longitude into [-180, 180). */
export function wrapLongitude(lon: number): number {
  const wrapped = ((lon + 180) % 360 + 360) % 360;
  return wrapped - 180;
}

/** Longitude (degrees) -> normalised mercator x in 0..1. */
export function lonToMercatorX(lon: number): number {
  return (lon + 180) / 360;
}

/** Latitude (degrees) -> normalised mercator y in 0..1, 0 at the north edge. */
export function latToMercatorY(lat: number): number {
  const phi = clampLatitude(lat) * DEG_TO_RAD;
  // ln(tan(phi) + sec(phi)) is the inverse Gudermannian; asinh(tan(phi)) is the
  // same value but avoids the blow-up of sec() near the poles.
  return 0.5 - Math.asinh(Math.tan(phi)) / (2 * Math.PI);
}

/** Normalised mercator x -> longitude (degrees). */
export function mercatorXToLon(mx: number): number {
  return mx * 360 - 180;
}

/** Normalised mercator y -> latitude (degrees). */
export function mercatorYToLat(my: number): number {
  return Math.atan(Math.sinh(Math.PI * (1 - 2 * my))) * RAD_TO_DEG;
}

/** Fractional tile x. Floor it for a tile index. */
export function lonToTileXf(lon: number, z: number): number {
  return lonToMercatorX(lon) * Math.pow(2, z);
}

/** Fractional tile y. Floor it for a tile index. */
export function latToTileYf(lat: number, z: number): number {
  return latToMercatorY(lat) * Math.pow(2, z);
}

/** Integer tile column containing this longitude. */
export function lonToTileX(lon: number, z: number): number {
  return Math.floor(lonToTileXf(lon, z));
}

/** Integer tile row containing this latitude. */
export function latToTileY(lat: number, z: number): number {
  return Math.floor(latToTileYf(lat, z));
}

/** Longitude of a tile column's west edge. */
export function tileXToLon(x: number, z: number): number {
  return mercatorXToLon(x / Math.pow(2, z));
}

/** Latitude of a tile row's north edge. */
export function tileYToLat(y: number, z: number): number {
  return mercatorYToLat(y / Math.pow(2, z));
}

/** Number of tiles per axis at this zoom. */
export function tilesPerAxis(z: number): number {
  return Math.pow(2, z);
}

/**
 * Ground distance covered by one pixel, in metres.
 *
 * Mercator is conformal, so scale is isotropic at a point but grows as
 * 1/cos(lat). This is the local scale, not an average over the tile.
 */
export function groundResolution(lat: number, z: number, tileSize = 256): number {
  return (EARTH_CIRCUMFERENCE * Math.cos(clampLatitude(lat) * DEG_TO_RAD)) /
    (tileSize * Math.pow(2, z));
}

/** East-west ground span of a whole tile at this latitude, in metres. */
export function tileSpanMetres(lat: number, z: number): number {
  return (EARTH_CIRCUMFERENCE * Math.cos(clampLatitude(lat) * DEG_TO_RAD)) / Math.pow(2, z);
}

/**
 * The zoom whose pixels are closest to a desired ground resolution.
 * Useful for picking a source zoom from a target metres-per-vertex.
 */
export function zoomForResolution(
  lat: number,
  metresPerPixel: number,
  tileSize = 256,
  minZoom = 0,
  maxZoom = 22,
): number {
  const z = Math.log2(
    (EARTH_CIRCUMFERENCE * Math.cos(clampLatitude(lat) * DEG_TO_RAD)) /
      (tileSize * metresPerPixel),
  );
  return Math.min(maxZoom, Math.max(minZoom, Math.round(z)));
}

/** Great-circle distance in metres between two lat/lon points (haversine). */
export function distanceMetres(
  aLat: number,
  aLon: number,
  bLat: number,
  bLon: number,
): number {
  const dLat = (bLat - aLat) * DEG_TO_RAD;
  const dLon = (bLon - aLon) * DEG_TO_RAD;
  const sinLat = Math.sin(dLat / 2);
  const sinLon = Math.sin(dLon / 2);
  const h =
    sinLat * sinLat +
    Math.cos(aLat * DEG_TO_RAD) * Math.cos(bLat * DEG_TO_RAD) * sinLon * sinLon;
  return 2 * EARTH_RADIUS * Math.asin(Math.min(1, Math.sqrt(h)));
}
